import { PBRMaterial, SceneLoader, TransformNode, type Vector3, type Scene } from "@babylonjs/core";
import "@babylonjs/loaders/glTF";
import { AnimationController } from "./AnimationController";

const MODEL_ROOT_URL = "/assets/models/";
const MODEL_FILE = "johnny-character.glb";

/**
 * Johnny, the island's castaway. Model sourced from PolyPizza (CC0,
 * "Beach Character" by Quaternius, retinted toward this project's art
 * direction) — see assets-pipeline/ASSET_SOURCING.md for full provenance.
 * Ships with a shared Quaternius animation library (Idle, Walk, Run, Wave,
 * Interact, Roll, plus unused combat clips); Phase 2 only starts Idle.
 */
export class JohnnyCharacter {
  readonly root: TransformNode;
  readonly animation: AnimationController;

  private constructor(root: TransformNode, animation: AnimationController) {
    this.root = root;
    this.animation = animation;
  }

  static async load(scene: Scene, position: Vector3): Promise<JohnnyCharacter> {
    const result = await SceneLoader.ImportMeshAsync("", MODEL_ROOT_URL, MODEL_FILE, scene);

    const root = new TransformNode("johnnyRoot", scene);
    for (const mesh of result.meshes) {
      if (!mesh.parent) mesh.parent = root;

      // The Blender->glTF export round-trip imported every material with
      // metallic=0.4, which is wrong for this character's flat-colored
      // cloth/skin/hair (an export heuristic artifact, not an authored
      // value — Blender's source materials aren't a metal workflow at
      // all). With no scene environment texture, that metallic fraction
      // reflects pure black, darkening the whole character uniformly.
      // Forced back to a pure dielectric response, matching how the
      // environment materials are handled (see IslandTerrain.ts).
      const material = mesh.material;
      if (material instanceof PBRMaterial) {
        material.metallic = 0;
        material.environmentIntensity = 0;
      }
    }
    root.position = position;
    // The source asset's front faces local +Z; the app's default camera
    // sits roughly opposite that, so an unrotated Johnny stands with his
    // back to the viewer on load. Turned to face generally toward camera.
    root.rotation.y = Math.PI;
    // Re-exporting this glb after adding the Sit/Sleep actions (Phase 5)
    // changed the exported root orientation — the model now loads lying on
    // its side (height along Z instead of Y) rather than standing. Root
    // cause not fully pinned down on the Blender side (a confusing,
    // seemingly non-deterministic interaction between the rig's quaternion
    // rotation mode and object-level transform state across repeated
    // headless script executions — see docs/Castaway Plan.md); corrected
    // here instead, verified empirically via screenshot rather than by
    // fully understanding the Blender-side cause.
    root.rotation.x = -Math.PI / 2;

    const animation = new AnimationController(result.animationGroups);
    animation.playIdle();

    return new JohnnyCharacter(root, animation);
  }

  dispose(): void {
    this.animation.dispose();
    this.root.dispose();
  }
}
